import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { login } from 'src/api/services/supabase/auth';
import { useAuth } from 'src/containers/contexts/auth-provider';
import { signInWithSSO } from '../../../../api/lib/supabase/auth';
import {
  SSOProvider,
  SupabaseProviderConfig,
} from '../../../../auth/supabase-provider-registry';
import supabaseAuthProviderService from '../../../../services/supabase-auth-provider.service';
import {
  EmailStepFormValues,
  PasswordStepFormValues,
  SSOProviderStepFormValues,
  useLoginFormValidationSchema,
} from '../../schema/use-form-validation-schema';
import { EmailStep } from '../organisms/login-steps/email-step';
import { PasswordStep } from '../organisms/login-steps/password-step';
import { SSOProvidersStep } from '../organisms/login-steps/sso-providers-step';

type LoginStep = 'email' | 'password' | 'ssoProvider';

export const Login = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const searchParams = useSearch({ from: '/(common)/login' });

  const [step, setStep] = useState<LoginStep>('email');
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);

  const {
    emailStepValidationSchema,
    passwordStepValidationSchema,
    ssoProviderStepValidationSchema,
  } = useLoginFormValidationSchema();

  const resolvers = {
    email: zodResolver(emailStepValidationSchema),
    password: zodResolver(passwordStepValidationSchema),
    ssoProvider: zodResolver(ssoProviderStepValidationSchema),
  };

  const form = useForm({
    resolver: resolvers[step],
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      email: '',
      password: '',
      ssoProviderId: '',
    },
  });

  const initiateSSOLogin = async (provider: SupabaseProviderConfig) => {
    //? SSO login

    if (!provider.ssoClient) {
      throw new Error('Provider is not a SSO client');
    }

    const hasMultipleSSOProviders = provider.ssoProvider.length > 1;

    if (hasMultipleSSOProviders) {
      setSsoProviders(provider.ssoProvider);
      // TODO: IMPLEMENT move to another screen
      // setStep('ssoProvider');
    } else {
      const response = await signInWithSSO(
        provider.ssoProvider[0].id,
        window.location.origin + '/',
      );

      if (response.data?.url) {
        // redirect the user to the identity provider's authentication flow
        window.location.href = response.data.url;
      }
    }
  };

  const onEmailSubmit = async ({ email }: EmailStepFormValues) => {
    supabaseAuthProviderService.clearPersistedProvider();

    let provider: SupabaseProviderConfig | null = null;

    const domain = email.split('@')[1];

    const hasProviderByDomain =
      supabaseAuthProviderService.hasProviderByDomain(domain);

    if (hasProviderByDomain) {
      provider = supabaseAuthProviderService.loadProviderByDomain(domain);
    } else {
      provider = supabaseAuthProviderService.loadDefaultProvider();
    }

    supabaseAuthProviderService.persistLoadedProvider();

    if (provider.ssoClient) {
      await initiateSSOLogin(provider);
    } else {
      setStep('password');
    }
  };

  const onPasswordSubmit = async ({
    email,
    password,
  }: PasswordStepFormValues) => {
    try {
      //temporary - loginWithPassword uses ENV supabase client!
      await login(email, password);
    } catch (error) {
      form.setError('root', { message: t('auth:invalidCredentials') });
    }
  };

  const onSSOSubmit = async ({ ssoProviderId }: SSOProviderStepFormValues) => {
    // TODO: handle multiple SSO providers/entras selection
    console.log({ ssoProviderId });

    // try {
    //   await loginWithSSO(ssoProviderId);
    // } catch (error) {
    //   form.setError('root', { message: t('auth:invalidCredentials') });
    // }
  };

  const returnUrl = decodeURIComponent(searchParams.returnUrl ?? '/');

  const handleBackButtonClick = () => setStep('email');

  if (!auth.session) {
    return (
      <FormProvider {...form}>
        {step === 'email' && <EmailStep onSubmit={onEmailSubmit} />}
        {step === 'password' && (
          <PasswordStep
            onSubmit={onPasswordSubmit}
            onBackButtonClick={handleBackButtonClick}
          />
        )}
        {step === 'ssoProvider' && (
          <SSOProvidersStep
            onSubmit={onSSOSubmit}
            ssoProviders={ssoProviders}
            onBackButtonClick={handleBackButtonClick}
          />
        )}
      </FormProvider>
    );
  }

  return <Navigate to={returnUrl} replace />;
};
